// src/lib/pdfExtract.js
// Shared browser-side PDF text extraction, used by every upload path that
// auto-fires auto-categorize-document. Supabase edge can't run pdfjs
// (worker limit / memory cap), so extraction happens here and the result
// rides along with the categorize request. Previously this only existed
// inline inside DealView's manual "Analyze" button — every other upload
// path (admin dropzone, DiligenceSection's dropzones, the seller portal)
// fired auto-categorize-document with no extracted_text/extracted_images
// at all, so any real-text PDF over 4MB (a normal bank statement, for
// instance) failed as "too large for vision... likely a scanned-image
// PDF" even though it had perfectly good extractable text the whole time.

let _pdfjsPromise = null;
function loadPdfjs() {
  if (!_pdfjsPromise) {
    _pdfjsPromise = (async () => {
      const pdfjs = await import('pdfjs-dist/build/pdf.mjs');
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.mjs?url')).default;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      return pdfjs;
    })();
  }
  return _pdfjsPromise;
}

// Extracts text from every page; if the PDF has no extractable text
// (scanned image), falls back to rendering the first few pages as JPEGs
// for Claude vision to read instead.
//
// A large scanned PDF (e.g. a multi-page depreciation schedule photocopied
// from paper) used to blow up this whole function on one bad page — a
// single getTextContent()/render()/toDataURL() throw for any page aborted
// everything, discarding whatever text or images WOULD have come back
// clean. That silently degraded to "no extractable text, too large for
// vision" on the server, which reads as "scanned PDF, run OCR" even when
// the real cause was a client-side rendering hiccup on one page. Each page
// is now independently best-effort so one bad page doesn't sink the rest.
async function extractPdfFromArrayBuffer(arrayBuffer) {
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
  const pieces = [];
  for (let p = 1; p <= doc.numPages; p++) {
    try {
      const page = await doc.getPage(p);
      const content = await page.getTextContent();
      pieces.push(content.items.map(it => it.str).join(' '));
    } catch (e) {
      console.warn(`PDF page ${p} text extraction failed:`, e.message);
    }
  }
  const text = pieces.join('\n\n').trim();

  let images = null;
  if (text.length < 200) {
    // A large multi-page scanned PDF (e.g. a 10MB+ depreciation schedule)
    // used to render up to 5 pages at up to 4000px each with no size check —
    // the resulting multi-MB base64 payload doesn't fail cleanly, it makes
    // the fetch() to auto-categorize-document itself fail ("Failed to
    // fetch", no HTTP response at all), because whatever gateway sits in
    // front of the edge function rejects an oversized request before the
    // function code ever runs (Supabase docs the function's own 256MB
    // memory / 2s CPU budget but not a request body limit — the practical
    // ceiling is lower and undocumented). Cap combined base64 size well
    // under any plausible limit instead, and stop adding pages once
    // (fewer, still-legible) pages fill the budget.
    const MAX_TOTAL_BASE64_CHARS = 3_500_000 // ~3.5MB of base64, ~2.6MB of actual image bytes
    images = []
    let totalChars = 0
    const maxPages = Math.min(doc.numPages, 5)
    for (let pi = 1; pi <= maxPages && totalChars < MAX_TOTAL_BASE64_CHARS; pi++) {
      try {
        const pg = await doc.getPage(pi)
        // Scale down for an unusually large page (a high-DPI scan can
        // otherwise produce a canvas past the browser's max dimension,
        // which throws and used to take the other pages down with it).
        const rawViewport = pg.getViewport({ scale: 1.5 })
        const scale = Math.min(1.5, 2200 / Math.max(rawViewport.width, rawViewport.height, 1))
        const viewport = pg.getViewport({ scale })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        await pg.render({ canvasContext: ctx, viewport }).promise
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7)
        if (totalChars + dataUrl.length > MAX_TOTAL_BASE64_CHARS && images.length > 0) {
          // Already have at least one page — stop here rather than risk
          // pushing the total payload past the limit.
          break
        }
        images.push(dataUrl)
        totalChars += dataUrl.length
      } catch (e) {
        console.warn(`PDF page ${pi} image render failed:`, e.message)
      }
    }
    if (images.length === 0) images = null
  }
  try { doc.destroy && doc.destroy(); } catch (e) { /* best-effort */ }
  return { text, images };
}

// Given a freshly-selected File (or a { mimeType, arrayBuffer } pair for
// already-uploaded files being re-analyzed from storage), returns the
// extra body fields to merge into an auto-categorize-document POST —
// {} for non-PDFs or on extraction failure (server falls back to its own
// vision path for anything <=4MB, or a clear error otherwise).
export async function extractPdfExtraFields(file) {
  const mimeType = file?.type || file?.mimeType;
  if (mimeType !== 'application/pdf') return {};
  try {
    const arrayBuffer = file.arrayBuffer ? await file.arrayBuffer() : file.arrayBufferData;
    const { text, images } = await extractPdfFromArrayBuffer(arrayBuffer);
    const extra = {};
    if (text && text.length >= 200) extra.extracted_text = text;
    if (images && images.length > 0) extra.extracted_images = images;
    return extra;
  } catch (e) {
    console.warn('Browser PDF extract failed:', e.message);
    return {};
  }
}

// Same rationale as extractPdfExtraFields, applied to spreadsheets: XLSX
// parsing (SheetJS) still ran entirely server-side in auto-categorize-
// document, which is the exact class of failure already fixed for PDFs
// here — a big/complex file blowing the edge function's memory limit
// (HTTP 546), not an application error. Parse client-side instead and
// send rendered CSV text, same as PDFs send extracted text.
export async function extractXlsxExtraFields(file) {
  const mimeType = file?.type || file?.mimeType;
  const name = file?.name || file?.original_name || '';
  const isXlsx =
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    /\.(xlsx|xls|xlsm)$/i.test(name);
  if (!isXlsx) return {};
  try {
    const XLSX = await import('xlsx');
    const arrayBuffer = file.arrayBuffer ? await file.arrayBuffer() : file.arrayBufferData;
    const wb = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
    const parts = [];
    for (const sheetName of wb.SheetNames) {
      const sheet = wb.Sheets[sheetName];
      if (!sheet) continue;
      const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
      if (csv.trim()) parts.push(`=== Sheet: ${sheetName} ===\n${csv}`);
    }
    let combined = parts.join('\n\n');
    if (!combined.trim()) return {};
    const maxChars = 60000;
    if (combined.length > maxChars) {
      combined = combined.slice(0, maxChars) + `\n\n[truncated — ${combined.length - maxChars} more characters]`;
    }
    return { extracted_text: `Spreadsheet "${name}" rendered as CSV per sheet:\n\n${combined}` };
  } catch (e) {
    console.warn('Browser XLSX extract failed:', e.message);
    return {};
  }
}
